import React, { useState, useEffect, useCallback } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Plus, Star, MessageSquare, ChevronLeft, ChevronRight, CheckCircle2, XCircle, Clock } from 'lucide-react';

const FB_TYPES = ['COMPLIMENT', 'CONCERN', 'SERVICE_QUALITY', 'POST_INCIDENT', 'GENERAL'];
const FB_STATUSES = ['NEW', 'UNDER_REVIEW', 'CLOSED'];
const STATUS_COLORS = { NEW: 'bg-blue-500 text-white', UNDER_REVIEW: 'bg-amber-500 text-white', CLOSED: 'bg-slate-500 text-white' };
const TYPE_COLORS = { COMPLIMENT: 'bg-green-100 text-green-700', CONCERN: 'bg-red-100 text-red-700', SERVICE_QUALITY: 'bg-purple-100 text-purple-700', POST_INCIDENT: 'bg-amber-100 text-amber-700', GENERAL: 'bg-slate-100 text-slate-700' };

function RatingStars({ value, onChange, readonly }) {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map(n => (
        <Star
          key={n}
          className={`w-5 h-5 cursor-pointer transition-colors ${n <= (value || 0) ? 'text-amber-400 fill-amber-400' : 'text-slate-300'}`}
          onClick={() => !readonly && onChange?.(n)}
          data-testid={`star-${n}`}
        />
      ))}
    </div>
  );
}

export default function FeedbackPage() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [showCreate, setShowCreate] = useState(false);
  const [closeDialog, setCloseDialog] = useState(null);
  const [closeResolution, setCloseResolution] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), pageSize: '20' });
      if (typeFilter !== 'all') params.append('feedbackType', typeFilter);
      if (statusFilter !== 'all') params.append('status', statusFilter);
      const { data } = await api.get(`/engagement/feedback?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch { toast.error('Failed to load feedback'); }
    finally { setLoading(false); }
  }, [page, typeFilter, statusFilter]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (form) => {
    try {
      await api.post('/engagement/feedback', form);
      toast.success('Feedback submitted'); setShowCreate(false); load();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
  };

  const handleClose = async () => {
    if (!closeDialog) return;
    try {
      await api.post(`/engagement/feedback/${closeDialog.id}/close`, { resolution: closeResolution });
      toast.success('Feedback closed'); setCloseDialog(null); setCloseResolution(''); load();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
  };

  const handleStatusChange = async (id, status) => {
    try {
      await api.put(`/engagement/feedback/${id}`, { status });
      toast.success('Status updated'); load();
    } catch (e) { toast.error(e.response?.data?.error?.message || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="feedback-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><MessageSquare className="w-6 h-6" />Feedback</h1>
        <Button onClick={() => setShowCreate(true)} data-testid="new-feedback-btn"><Plus className="w-4 h-4 mr-2" />New Feedback</Button>
      </div>

      <div className="flex gap-3">
        <Select value={typeFilter} onValueChange={v => { setTypeFilter(v); setPage(1); }}>
          <SelectTrigger className="w-44" data-testid="type-filter"><SelectValue placeholder="Type" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Types</SelectItem>{FB_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={v => { setStatusFilter(v); setPage(1); }}>
          <SelectTrigger className="w-36" data-testid="status-filter"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All</SelectItem>{FB_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
        </Select>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : (
        <div className="space-y-2">
          {items.map(f => (
            <Card key={f.id}>
              <CardContent className="flex items-center justify-between py-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <Badge className={STATUS_COLORS[f.status]}>{f.status?.replace(/_/g, ' ')}</Badge>
                    <Badge className={TYPE_COLORS[f.feedbackType] || ''}>{f.feedbackType?.replace(/_/g, ' ')}</Badge>
                    {f.rating && <RatingStars value={f.rating} readonly />}
                  </div>
                  <p className="font-medium mt-1">{f.title}</p>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {f.participantName && <span className="mr-2">{f.participantName}</span>}
                    <span>{f.submittedByType}</span>
                    <span className="ml-3">{new Date(f.createdAt).toLocaleDateString()}</span>
                  </div>
                  {f.message && <p className="text-sm text-muted-foreground mt-1 line-clamp-2">{f.message}</p>}
                  {f.resolution && <p className="text-xs text-green-600 mt-1">Resolution: {f.resolution}</p>}
                </div>
                <div className="flex gap-1 shrink-0">
                  {f.status === 'NEW' && <Button size="sm" variant="outline" onClick={() => handleStatusChange(f.id, 'UNDER_REVIEW')} data-testid={`review-${f.id}`}>Review</Button>}
                  {f.status !== 'CLOSED' && <Button size="sm" variant="outline" onClick={() => { setCloseDialog(f); setCloseResolution(''); }} data-testid={`close-${f.id}`}>Close</Button>}
                </div>
              </CardContent>
            </Card>
          ))}
          {items.length === 0 && <p className="text-center text-muted-foreground py-8">No feedback found</p>}
        </div>
      )}

      <div className="flex justify-between items-center">
        <span className="text-sm text-muted-foreground">{total} total</span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}><ChevronLeft className="w-4 h-4" /></Button>
          <span className="text-sm py-1">Page {page}</span>
          <Button size="sm" variant="outline" disabled={items.length < 20} onClick={() => setPage(p => p + 1)}><ChevronRight className="w-4 h-4" /></Button>
        </div>
      </div>

      {/* Create Dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>Submit Feedback</DialogTitle></DialogHeader>
          <CreateFeedbackForm onSave={handleCreate} onCancel={() => setShowCreate(false)} />
        </DialogContent>
      </Dialog>

      {/* Close Dialog */}
      <Dialog open={!!closeDialog} onOpenChange={() => setCloseDialog(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Close Feedback: {closeDialog?.title}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Resolution</Label><Textarea data-testid="close-resolution" value={closeResolution} onChange={e => setCloseResolution(e.target.value)} placeholder="Describe how this was resolved..." rows={3} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialog(null)}>Cancel</Button>
            <Button data-testid="confirm-close-btn" onClick={handleClose}>Close Feedback</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CreateFeedbackForm({ onSave, onCancel }) {
  const [participants, setParticipants] = useState([]);
  const [form, setForm] = useState({ participantId: '', submittedByType: 'FAMILY', feedbackType: 'GENERAL', rating: null, title: '', message: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    api.get('/participants?page=1&pageSize=50').then(r => setParticipants(r.data.items || r.data.data || [])).catch(() => {});
  }, []);

  return (
    <div className="space-y-3">
      <div><Label>Participant</Label>
        <Select value={form.participantId} onValueChange={v => set('participantId', v)}>
          <SelectTrigger data-testid="fb-participant"><SelectValue placeholder="Select participant" /></SelectTrigger>
          <SelectContent>{participants.map(p => <SelectItem key={p.id} value={p.id}>{p.first_name} {p.last_name}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><Label>Type</Label>
          <Select value={form.feedbackType} onValueChange={v => set('feedbackType', v)}>
            <SelectTrigger data-testid="fb-type"><SelectValue /></SelectTrigger>
            <SelectContent>{FB_TYPES.map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div><Label>Submitted By</Label>
          <Select value={form.submittedByType} onValueChange={v => set('submittedByType', v)}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>{['PARTICIPANT', 'FAMILY', 'GUARDIAN', 'PORTAL_USER'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      </div>
      <div><Label>Rating</Label><RatingStars value={form.rating} onChange={v => set('rating', v)} /></div>
      <div><Label>Title</Label><Input data-testid="fb-title" value={form.title} onChange={e => set('title', e.target.value)} /></div>
      <div><Label>Message</Label><Textarea data-testid="fb-message" value={form.message} onChange={e => set('message', e.target.value)} rows={4} /></div>
      <div className="flex justify-end gap-2">
        <Button variant="outline" onClick={onCancel}>Cancel</Button>
        <Button data-testid="submit-feedback-btn" onClick={() => onSave(form)} disabled={!form.participantId || !form.title || !form.message}>Submit</Button>
      </div>
    </div>
  );
}

export function PortalFeedbackPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ feedbackType: 'GENERAL', rating: null, title: '', message: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  useEffect(() => {
    setLoading(true);
    api.get('/portal/feedback?pageSize=50')
      .then(r => setItems(r.data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const handleSubmit = async () => {
    try {
      await api.post('/portal/feedback', form);
      toast.success('Feedback submitted'); setShowCreate(false);
      setForm({ feedbackType: 'GENERAL', rating: null, title: '', message: '' });
      const r = await api.get('/portal/feedback?pageSize=50');
      setItems(r.data.items || []);
    } catch (e) { toast.error(e.response?.data?.error?.message || e.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6" data-testid="portal-feedback-page">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight">My Feedback</h1>
        <Button onClick={() => setShowCreate(true)} data-testid="portal-new-feedback-btn"><Plus className="w-4 h-4 mr-2" />Give Feedback</Button>
      </div>

      {loading ? <p className="text-muted-foreground">Loading...</p> : items.length === 0 ? (
        <p className="text-center text-muted-foreground py-8">You haven't submitted any feedback yet</p>
      ) : (
        <div className="space-y-2">
          {items.map(f => (
            <Card key={f.id}>
              <CardContent className="py-3">
                <div className="flex items-center gap-2">
                  <Badge className={STATUS_COLORS[f.status]}>{f.status?.replace(/_/g, ' ')}</Badge>
                  <Badge className={TYPE_COLORS[f.feedbackType] || ''}>{f.feedbackType?.replace(/_/g, ' ')}</Badge>
                  {f.rating && <RatingStars value={f.rating} readonly />}
                </div>
                <p className="font-medium mt-1">{f.title}</p>
                <p className="text-sm text-muted-foreground mt-1">{f.message}</p>
                {f.resolution && <p className="text-xs text-green-600 mt-2">Resolution: {f.resolution}</p>}
                <p className="text-xs text-muted-foreground mt-1">{new Date(f.createdAt).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader><DialogTitle>Give Feedback</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Type</Label>
              <Select value={form.feedbackType} onValueChange={v => set('feedbackType', v)}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{['COMPLIMENT', 'CONCERN', 'SERVICE_QUALITY', 'GENERAL'].map(t => <SelectItem key={t} value={t}>{t.replace(/_/g, ' ')}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Rating</Label><RatingStars value={form.rating} onChange={v => set('rating', v)} /></div>
            <div><Label>Title</Label><Input data-testid="portal-fb-title" value={form.title} onChange={e => set('title', e.target.value)} /></div>
            <div><Label>Message</Label><Textarea data-testid="portal-fb-message" value={form.message} onChange={e => set('message', e.target.value)} rows={4} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button data-testid="portal-submit-feedback" onClick={handleSubmit} disabled={!form.title || !form.message}>Submit</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
